import { convertLocalDateDTOToDate } from './dateUtil';
import type { LocalDateDTO } from 'gw-edge-client/dist/types/dto/pc/edge/time';
import { EdiCaseStatusMap } from '../pages/CheckoutPage/steps/DatesAndCancellationStep';
import type {
    AdultAccidentDTO,
    ChildAccidentDTO,
    YoungAccidentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/accident';
import type {
    CatDTO,
    DogDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/pet';
import type { MachineryDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mac';
import type { YouthContentDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/content';
import type { AgriculturePropertyDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/pra';
import type { EDICancellationReason_Top } from 'gw-edge-client/dist/types/dto/pc/typekey/edicancellationreason_top.typekey';
import type {
    CoverableDTO,
    CoverageDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote/';
import type { ExternaleCaseDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/policyjob/edi/dto/';
import type { CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';
import { isAccCoverableForOtherPerson } from './coverablesUtil';
import { COVERABLE_CODE, LOB_CODE } from '../constants/coverable';

const CancellationReason: Record<string, EDICancellationReason_Top> = {
    ExpirationDate: '285',
    Claim: '286',
    ChangeOfOwnership: '287',
    OtherDate: '288',
    CustomerRelease: '292',
    Regret: '294',
    ShortRate: '296'
};

const mapCoverableLobCodesToExtCaseLobCodes = (lobCode?: string): string | undefined => {
    switch (lobCode) {
        case 'PRA':
            return 'PRAPropertyLine_Top';
        case 'MAC':
            return 'MACMachineryLine_Top';
        default:
            return undefined;
    }
};

const coverableToExternalCase = (
    coverable: CoverableDTO | undefined,
    externalCases: ExternaleCaseDTO[]
): ExternaleCaseDTO | undefined => {
    const extCaseLobCode = mapCoverableLobCodesToExtCaseLobCodes(coverable?.lobCode);
    if (extCaseLobCode) {
        const lobCases = externalCases.filter((extCase) => extCase.lobCode === extCaseLobCode);
        return earliestCancelledCase(lobCases);
    }

    return externalCases.find(
        (externalCase) =>
            // substring is required to match external case lobcodes ('PRAPropertyLine_Top')
            // against coverable lobcodes ('PRA')
            externalCase.lobCode?.substring(0, 3) === coverable?.lobCode &&
            externalCase.linkedEntityFixedId === coverable?.fixedId
    );
};

const earliestCancelledCase = (externalCases: ExternaleCaseDTO[]): ExternaleCaseDTO | undefined => {
    if (externalCases.length === 0) {
        return undefined;
    }
    return externalCases.reduce((caseA, caseB) => {
        if (!caseA?.cancellationDate) {
            return caseB;
        } else if (!caseB?.cancellationDate) {
            return caseA;
        } else {
            return convertLocalDateDTOToDate(caseA.cancellationDate) >
                convertLocalDateDTOToDate(caseB.cancellationDate)
                ? caseB
                : caseA;
        }
    });
};

const externalCaseToCoverable = (
    externalCase: ExternaleCaseDTO | undefined,
    allCoverables: CoverableDTO[]
): CoverableDTO | undefined => {
    if (externalCase?.lobCode === 'PRAPropertyLine_Top') {
        const coverable = allCoverables.find(
            (coverable) => coverable.lobCode === 'PRA'
        ) as AgriculturePropertyDTO;
        return coverable.propertyLocations?.find(
            (location) => location.fixedId === externalCase?.linkedEntityFixedId
        );
    }

    if (externalCase?.lobCode === 'MACMachineryLine_Top') {
        const coverable = allCoverables.find(
            (coverable) => coverable.lobCode === 'MAC'
        ) as MachineryDTO;

        const unspecifiedMachinery =
            externalCase?.linkedEntityFixedId === coverable.unspecifiedMachinery?.fixedId
                ? coverable.unspecifiedMachinery
                : null;

        if (unspecifiedMachinery) {
            return unspecifiedMachinery;
        }

        return coverable.specifiedMachinery?.find(
            (machine) => machine.fixedId === externalCase?.linkedEntityFixedId
        );
    }

    return allCoverables.find(
        (coverable) =>
            // substring is required to match external case lobcodes ('PRAPropertyLine_Top')
            // against coverable lobcodes ('PRA')
            coverable.lobCode === externalCase?.lobCode?.substring(0, 3) &&
            coverable.fixedId === externalCase?.linkedEntityFixedId
    );
};

const shouldRenderEdiRadioButtons = (
    cancellationReason: string | undefined,
    flavourName: string | undefined,
    isCVR: boolean | undefined
): boolean => {
    const isHouseFlavour =
        flavourName === 'Husforsikring' || flavourName === 'Fritidshusforsikring';
    return (
        isModifiableCancellationReason(cancellationReason, flavourName) &&
        !(isHouseFlavour && cancellationReason === CancellationReason.ChangeOfOwnership) &&
        !isCVR
    );
};

const shouldRenderPrhDatePicker = (
    cancellationReason: string | undefined,
    flavourName: string | undefined
): boolean => {
    return (
        (flavourName === 'Husforsikring' || flavourName === 'Fritidshusforsikring') &&
        cancellationReason === CancellationReason.ChangeOfOwnership
    );
};

const isModifiableCancellationReason = (
    cancellationReason: string | undefined,
    flavourName: string | undefined
): boolean => {
    return (
        cancellationReason === undefined ||
        cancellationReason === CancellationReason.ShortRate ||
        cancellationReason === CancellationReason.ExpirationDate ||
        ((flavourName === 'Husforsikring' || flavourName === 'Fritidshusforsikring') &&
            cancellationReason === CancellationReason.ChangeOfOwnership)
    );
};

const isAnyEdiCancellationCasePending = (externalCases: ExternaleCaseDTO[]): boolean => {
    return externalCases.some(
        (cancellation) => cancellation.status === EdiCaseStatusMap.PendingCompletion
    );
};

const updateExternalCaseDTOProperties = (
    externalCaseDTOList: ExternaleCaseDTO[],
    caseId: string[],
    updateValue: ExternaleCaseDTO
): ExternaleCaseDTO[] => {
    return externalCaseDTOList?.map((item) => {
        if (item.caseId && caseId.includes(item.caseId)) {
            return {
                ...item,
                ...updateValue
            };
        }
        return item;
    });
};

const calculateAge = (dob: LocalDateDTO | undefined, date: LocalDateDTO): number => {
    const birthYear = dob?.year ?? 0;
    const birthMonth = dob?.month ?? 0;
    const birthDay = dob?.day ?? 0;
    const dateYear = date.year ?? 0;
    const dateMonth = date.month ?? 0;
    const dateDay = date.day ?? 0;

    let age = dateYear - birthYear;

    if (age < 0) {
        return 0;
    }

    if (age > 0 && (dateMonth < birthMonth || (dateMonth === birthMonth && dateDay < birthDay))) {
        age--;
    }

    return age;
};

const isCoverableDogDTO = (coverable: CoverableDTO): coverable is DogDTO => {
    return coverable.flavourName === 'Hundeforsikring';
};

const isCoverableCatDTO = (coverable: CoverableDTO): coverable is CatDTO => {
    return coverable.flavourName === 'Katteforsikring';
};

const isCoverableAdultAccDTO = (coverable: CoverableDTO): coverable is AdultAccidentDTO => {
    return coverable.flavourName === 'Ulykkesforsikring';
};

const isCoverableChildAccDTO = (coverable: CoverableDTO): coverable is ChildAccidentDTO => {
    return coverable.flavourName === 'Børneforsikring';
};

const isCoverableYoungAccDTO = (coverable: CoverableDTO): coverable is YoungAccidentDTO => {
    return coverable.flavourName === 'Ulykkesforsikring til unge';
};

const isCoverableYouthContentDTO = (coverable: CoverableDTO): coverable is YouthContentDTO => {
    return coverable.flavourName === 'Indboforsikring til unge';
};

const isCoverableCarDTO = (coverable: CoverableDTO): coverable is CarDTO => {
    return coverable.flavourName === 'Bilforsikring';
};

const isLongtermPrivateLob = (lobCode: string): boolean => {
    return !isAgricultureLob(lobCode) && !isSingleTripTravelOrCancellationLob(lobCode);
};

const isAgricultureLob = (lobCode: string): boolean => {
    const coverableLobCode = lobCode?.substring(0, 3);
    return (
        coverableLobCode === 'PRA' ||
        coverableLobCode === 'MAC' ||
        coverableLobCode === 'CTP' ||
        coverableLobCode === 'WOC' ||
        coverableLobCode === 'LBV' ||
        coverableLobCode === 'CTV'
    );
};

const isSingleTripTravelOrCancellationLob = (lobCode: string): boolean => {
    const coverableLobCode = lobCode?.substring(0, 3);
    return coverableLobCode === 'STT' || coverableLobCode === 'STC';
};

const shouldHideEdiChooseStatus = (lobCode: string): boolean => {
    const coverableLobCode = lobCode?.substring(0, 3);
    return coverableLobCode === 'PRA' || coverableLobCode === 'MAC';
};

const isDefaultShortRateReason = (cancellationReason: string): boolean => {
    return cancellationReason === CancellationReason.ShortRate;
};

const isDefaultReasonExpirationDate = (cancellationReason: string): boolean => {
    return cancellationReason === CancellationReason.ExpirationDate;
};

const containsNeedDOBValidationCoverages = (coverage: CoverageDTO): boolean => {
    return (
        coverage.type === 'PETHealthCov' ||
        coverage.type === 'PETHealthPlusCov' ||
        coverage.type === 'PETLifeCov'
    );
};
const getYouthEffectiveDate = (date: LocalDateDTO): LocalDateDTO => {
    const effectiveDate = new Date(date.year ?? 0, date.month ?? 0, date.day ?? 0);
    // for youth, it is not allowed to buy insurance 40 days before the 28th birthday
    effectiveDate.setDate(effectiveDate.getDate() + 40);
    return {
        year: effectiveDate.getFullYear(),
        month: effectiveDate.getMonth(),
        day: effectiveDate.getDate()
    };
};

const dateHasValidationErrors = (
    date: LocalDateDTO,
    selectedCoverable: CoverableDTO | undefined,
    allCoverables: CoverableDTO[],
    isStartDate: boolean
): boolean => {
    if (!selectedCoverable) {
        return false;
    }

    if (isCoverableDogDTO(selectedCoverable)) {
        const maxAgeOfDog = 7;
        const doesDogNeedToValidateDOB = (selectedCoverable.coverages ?? []).some(
            (coverage) => containsNeedDOBValidationCoverages(coverage) && coverage.selected
        );
        return (
            doesDogNeedToValidateDOB &&
            calculateAge(selectedCoverable.dateOfBirth, date) >= maxAgeOfDog
        );
    }

    if (isCoverableCatDTO(selectedCoverable)) {
        const maxAgeOfCat = 7;
        return calculateAge(selectedCoverable.dateOfBirth, date) >= maxAgeOfCat;
    }

    if (isCoverableAdultAccDTO(selectedCoverable)) {
        const maxAgeOfAdult = 79;
        const hasContentCoverables = allCoverables.some(
            (coverable) =>
                coverable.flavourName === 'Indboforsikring til unge' ||
                coverable.flavourName === 'Indboforsikring'
        );
        return (
            calculateAge(selectedCoverable.dateOfBirth, date) > maxAgeOfAdult &&
            (isStartDate || !hasContentCoverables)
        );
    }

    if (isCoverableChildAccDTO(selectedCoverable)) {
        const maxAgeOfChild = 17;
        return calculateAge(selectedCoverable.dateOfBirth, date) > maxAgeOfChild;
    }

    if (isCoverableYoungAccDTO(selectedCoverable)) {
        const maxAgeOfInsuredPerson = 27;
        const effectiveDateDTO = getYouthEffectiveDate(date);
        return (
            calculateAge(selectedCoverable.dateOfBirth, effectiveDateDTO) > maxAgeOfInsuredPerson
        );
    }

    if (isCoverableYouthContentDTO(selectedCoverable)) {
        const maxAgeOfOldestInTheHousehold = 27;
        const effectiveDateDTO = getYouthEffectiveDate(date);
        return (
            calculateAge(selectedCoverable.dobOfOldestInHousehold, effectiveDateDTO) >
            maxAgeOfOldestInTheHousehold
        );
    }
    if (isCoverableCarDTO(selectedCoverable)) {
        const MVPLeasingReturnCoverage: CoverageDTO | undefined = selectedCoverable.coverages?.find(
            (coverage) => coverage.type === 'MVPLeasingcarReturn' && coverage.selected
        );
        if (
            MVPLeasingReturnCoverage === undefined ||
            MVPLeasingReturnCoverage.effectiveDate === undefined
        ) {
            return false;
        }
        const isCoverageLaterInForce =
            convertLocalDateDTOToDate(date).getTime() -
                convertLocalDateDTOToDate(MVPLeasingReturnCoverage.effectiveDate).getTime() <
            0;

        const coverageEffectiveDate = isCoverageLaterInForce
            ? MVPLeasingReturnCoverage.effectiveDate
            : date;

        if (coverageEffectiveDate === undefined || selectedCoverable.leasingEndDate === undefined) {
            return false;
        }
        const effectiveDateDifference = new Date(
            convertLocalDateDTOToDate(selectedCoverable.leasingEndDate).getTime() -
                convertLocalDateDTOToDate(coverageEffectiveDate).getTime()
        );
        const effectiveDateDifferenceInYears = effectiveDateDifference.getFullYear() - 1970;
        return effectiveDateDifferenceInYears < 2;
    }

    return false;
};

const filterAccEDICasesForOtherPerson = (
    externalCaseList: ExternaleCaseDTO[],
    coverableList: CoverableDTO[]
): ExternaleCaseDTO[] => {
    return externalCaseList.filter((ediCase) => {
        const coverable = externalCaseToCoverable(ediCase, coverableList);
        return coverable && !isAccCoverableForOtherPerson(coverable);
    });
};

const isCarWithTilgangReceived = (coverable: CoverableDTO): boolean => {
    if (coverable.lobCode === 'MVP') {
        const car = coverable as CarDTO;
        return !!car.hasTilgangReceived;
    }
    return false;
};

const getPetYoungerThan8WeeksName = (
    externalCaseDTOList: ExternaleCaseDTO[],
    allCoverables: CoverableDTO[]
): string | undefined => {
    let youngPet: string | undefined;
    externalCaseDTOList.find((externalCase) => {
        const coverable = externalCaseToCoverable(externalCase, allCoverables);
        if (coverable && coverable.lobCode === LOB_CODE.PET) {
            let pet;
            if (coverable.flavourCode === COVERABLE_CODE.dog) {
                pet = coverable as DogDTO;
            } else if (coverable.flavourCode === COVERABLE_CODE.cat) {
                pet = coverable as CatDTO;
            }
            const startDate = externalCase.cancellationDate ?? externalCase.startDate;
            if (!pet?.dateOfBirth || !startDate) {
                return false;
            }
            const dateOfBirth = convertLocalDateDTOToDate(pet.dateOfBirth);
            const effectiveDate = convertLocalDateDTOToDate(startDate);
            const eightWeeksInDays = 8 * 7;
            const eightWeekBirthday = new Date(dateOfBirth);
            eightWeekBirthday.setDate(eightWeekBirthday.getDate() + eightWeeksInDays);
            if (effectiveDate < eightWeekBirthday) {
                youngPet = pet.name;
                return true;
            }
        }
        return false;
    });
    return youngPet;
};

const isCarWithoutLicensePlates = (
    externalCaseDTOList: ExternaleCaseDTO[],
    allCoverables: CoverableDTO[]
): boolean =>
    externalCaseDTOList.some((externalCase) => {
        const coverable = externalCaseToCoverable(externalCase, allCoverables);
        if (coverable && coverable.lobCode === 'MVP') {
            const car = coverable as CarDTO;
            return !car.registrationNumber && externalCase.otherCompany;
        }
        return false;
    });

export {
    CancellationReason,
    coverableToExternalCase,
    externalCaseToCoverable,
    isModifiableCancellationReason,
    updateExternalCaseDTOProperties,
    isAnyEdiCancellationCasePending,
    dateHasValidationErrors,
    isLongtermPrivateLob,
    isAgricultureLob,
    isSingleTripTravelOrCancellationLob,
    shouldHideEdiChooseStatus,
    isDefaultShortRateReason,
    isDefaultReasonExpirationDate,
    shouldRenderPrhDatePicker,
    shouldRenderEdiRadioButtons,
    filterAccEDICasesForOtherPerson,
    isCarWithTilgangReceived,
    isCarWithoutLicensePlates,
    getPetYoungerThan8WeeksName
};
